import { ArrowRight, AtLink, AtLinkProps } from '../../atoms'
import { AspectRatio } from '../../molecules'
import { cn } from '../../utils/cn'
import { makeCtaAnalyticsClickHandler, useCtaAnalytics } from '../../utils/cta-analytics'
import { Media, OrMedia } from '../or-media'

export interface contentItems {
  title?: string
  body: string
  link: AtLinkProps
  hasBottomPadding?: boolean
  onTrackClick?: (contentHeadline: string, targetUrl: string) => void
}

type OrContentBlockAOrientation = 'normal' | 'reverse'
type OrContentBlockAVariant = 'full' | 'top-half' | 'bottom-half'
type OrContentBlockABackground = 'gray' | 'none'

export interface OrContentBlockAProps {
  title?: string
  media?: Media[]
  contents: contentItems[]
  aspectRatio?: AspectRatio
  orientation?: OrContentBlockAOrientation
  variant?: OrContentBlockAVariant
  background?: OrContentBlockABackground
  onTrackClick?: (contentHeadline: string, targetUrl: string) => void
}

export const OrContentBlockA = ({
  title,
  media,
  contents,
  orientation = 'normal',
  variant = 'full',
  background = 'gray',
  onTrackClick,
}: OrContentBlockAProps) => {
  const showTopHalf = Boolean(variant === 'top-half' || variant === 'full')
  const showBottomHalf = Boolean(variant === 'bottom-half' || variant === 'full')
  const contentTopHalf = getContentTopHalf(contents)
  const contentBottomHalf = getContentBottomHalf(contents, variant)
  const isNormal = Boolean(orientation === 'normal')
  const isReverse = Boolean(orientation === 'reverse')

  return (
    <section className={background === 'gray' ? 'bg-neutrals-300' : ''}>
      <div
        className={cn(
          'relative flex mx-auto px-4 py-8 sm:p-8 slg:px-12 lg:px-[10.438rem]',
          showTopHalf && showBottomHalf && 'lg:max-w-screen-lg',
          isReverse && 'flex-col-reverse slg:flex-row-reverse slg:gap-7.5',
          isNormal && 'flex-col slg:flex-row slg:gap-7.5',
          background === 'none' && '!pb-0'
        )}
      >
        <div className={cn('lg:w-[33.625rem] slg:flex-1', isReverse && 'slg:pl-8')}>
          {showTopHalf && (
            <>
              <div className="h-0.5 w-12 bg-secondary-700 mb-6" aria-hidden />
              {title ? (
                <h2 className="text-neutrals-900 font-medium mb-10 lg:mb-16 text-24 slg:text-32 font-heading leading-[1.3]">
                  {title}
                </h2>
              ) : (
                <div className="mb-10 lg:mb-16" />
              )}
            </>
          )}
          {showBottomHalf && (
            <>
              <div aria-hidden className="lg:h-[396px]" />
              <div
                className={cn(
                  'lg:absolute bg-neutrals-white h-[13.5rem] slg:h-[24.785rem] sm:w-[71.25%] slg:w-auto lg:w-[39.625rem] -mx-4 sm:mx-0 sm:-ml-8 slg:pr-0 slg:-ml-16 lg:ml-0',
                  showTopHalf && showBottomHalf && 'bottom-8',
                  !showTopHalf && showBottomHalf && 'bottom-16 top-16 lg:bottom-8 lg:top-8',
                  isReverse && 'flex flex-col items-start slg:items-end right-0',
                  isNormal && 'flex flex-col items-start slg:items-start left-0'
                )}
              >
                {media && media[0] && <OrMedia {...media[0]} />}
                {/* <MlImages unsetMaxDimensions images={media.slice(0, 1)} aspectRatio={aspectRatio} /> */}
              </div>
            </>
          )}
        </div>
        <div
          className={cn(
            'slg:flex-1',
            showBottomHalf && 'pt-6 slg:pt-0 slg:flex slg:flex-col slg:justify-center slg:items-center'
          )}
        >
          {showTopHalf && contentTopHalf && (
            <div className={!showBottomHalf ? 'slg:pt-[1.625rem]' : ''}>
              <ContentItem
                title={contentTopHalf?.title}
                body={contentTopHalf?.body}
                link={contentTopHalf?.link}
                hasBottomPadding={showBottomHalf && contentBottomHalf !== undefined}
                onTrackClick={onTrackClick}
              />
            </div>
          )}
          {showBottomHalf && contentBottomHalf && (
            <ContentItem
              title={contentBottomHalf?.title}
              body={contentBottomHalf?.body}
              link={contentBottomHalf?.link}
              hasBottomPadding={background !== 'none'}
              onTrackClick={onTrackClick}
            />
          )}
        </div>
      </div>
    </section>
  )
}

const ContentItem = ({ title, body, link, hasBottomPadding = false, onTrackClick }: contentItems) => {
  const hasLink = Boolean(link && Object.keys(link).length > 0)
  const { logClick } = useCtaAnalytics()

  return (
    <div className={cn('slg:w-[23.323rem] lg:w-[33.625rem]', title && hasBottomPadding && 'lg:pt-8')}>
      {title && (
        <div className="mb-6 lg:px-0">
          {<h3 className="text-neutrals-900 font-medium mb-4 text-24 font-heading leading-[1.3]">{title}</h3>}
        </div>
      )}
      <div className="mb-8 lg:px-0">
        <p className="text-neutrals-800 text-16 leading-1.5">{body}</p>
      </div>
      {hasLink && (
        <div className={cn('lg:px-0', hasBottomPadding ? 'mb-14' : 'mb-0')}>
          <AtLink
            {...link}
            onClick={makeCtaAnalyticsClickHandler(
              logClick,
              {
                text: link.title ?? '',
                href: link.actionTarget ?? '',
                style: 'text link',
                component: 'ContentBlockA',
              },
              (event) => {
                if (onTrackClick) onTrackClick(title ?? 'not available', link.actionTarget || '')
                if (link.onClick) link.onClick(event)
              }
            )}
            IconRightRef={link.IconRightRef || ArrowRight}
          />
        </div>
      )}
    </div>
  )
}

const getContentTopHalf = (contents: contentItems[]) => {
  return contents[0] || undefined
}

const getContentBottomHalf = (contents: contentItems[], variant: OrContentBlockAVariant) => {
  if (variant === 'bottom-half' && contents.length > 1) {
    return contents[1] || undefined
  } else if (variant === 'bottom-half' && contents.length === 1) {
    return contents[0] || undefined
  } else if (variant === 'top-half') {
    return undefined
  } else if (variant === 'full') {
    return contents[1] || undefined
  }

  return undefined
}
